import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../api/auth';

type Status = 'idle' | 'changing' | 'error' | 'success';

export const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('changing');
    setErrorMessage(null);
    setSuccessMessage(null);

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setErrorMessage('New passwords do not match');
      return;
    }

    try {
      const result = await authService.changePassword(currentPassword, newPassword);
      if (result.success) {
        setStatus('success');
        setSuccessMessage('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // Redirect to profile after 3 seconds
        setTimeout(() => {
          navigate('/profile', { replace: true });
        }, 3000);
      } else {
        setStatus('error');
        setErrorMessage(result.error?.message || 'Failed to change password');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'changing':
        return (
          <div className="verification-status">
            <h3>Changing Password</h3>
            <p>Please wait while we update your password...</p>
            <div className="loading-spinner mt-4"></div>
          </div>
        );
      case 'success':
        return (
          <div className="verification-status success-message">
            <h3>Password Changed Successfully!</h3>
            <p>{successMessage}</p>
            <div className="has-text-centered mt-4">
              <a href="/profile" className="button is-primary">Go to Profile</a>
            </div>
          </div>
        );
      case 'error':
        return (
          <div className="verification-status error-message">
            <h3>Password Change Failed</h3>
            <p>{errorMessage}</p>
            <div className="has-text-centered mt-4">
              <button
                className="button is-primary"
                onClick={() => setStatus('idle')}
              >
                Try Again
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div className="auth-content">
            <div className="auth-header">
              <h1 className="hero-title">Change Password</h1>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {errorMessage && (
                <div className="form-message is-error">
                  {errorMessage}
                </div>
              )}
              {successMessage && (
                <div className="form-message is-success">
                  {successMessage}
                </div>
              )}
              <div className="form-actions">
                <button
                  type="submit"
                  className="button is-primary"
                  disabled={status === 'changing'}
                >
                  {status === 'changing' ? (
                    <div className="loading-spinner" />
                  ) : (
                    'Change Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        );
    }
  };

  return (
    <div className="scientia-container">
      <div className="scientia-card">
        <div className="scientia-card__content">
          <h2 className="title has-text-centered mb-4">Change Password</h2>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
